import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';

import { useAuth } from '../../../contexts/AuthContext';
import logger from '../../../logging/logger';
import { NamePromptModal } from '../dashboards/NamePromptModal';
import type { MarketMonitorDashboardState } from '../dashboards/MarketMonitorDashboardProvider';
import { getDashboardSchedule, upsertDashboardSchedule } from './scheduleApi';
import { ScheduleEmailModal, type ScheduleEmailSubmitArgs } from './ScheduleEmailModal';
import type { EmailSchedule } from './scheduleTypes';
import styles from './ScheduleEmailButton.module.css';

type Toast =
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string }
  | null;

interface SaveAsPrompt {
  open: boolean;
  initialValue: string;
}

export interface ScheduleEmailButtonProps {
  state: MarketMonitorDashboardState;
}

export const ScheduleEmailButton: React.FC<ScheduleEmailButtonProps> = ({ state }) => {
  const { userUuid, userEmail, getToken } = useAuth();
  const {
    activeId,
    activeName,
    isDirty,
    isSaving,
    isLoadingActive,
    save,
    saveAs,
  } = state;

  const [scheduleModalOpen, setScheduleModalOpen] = useState<boolean>(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState<boolean>(false);
  const [saveAsPrompt, setSaveAsPrompt] = useState<SaveAsPrompt>({
    open: false,
    initialValue: '',
  });
  const [existing, setExisting] = useState<EmailSchedule | null>(null);
  const [isLoadingExisting, setIsLoadingExisting] = useState<boolean>(false);
  const [toast, setToast] = useState<Toast>(null);

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(id);
  }, [toast]);

  const openScheduleModal = useCallback(
    async (dashboardId: string) => {
      setScheduleModalOpen(true);
      setIsLoadingExisting(true);
      setExisting(null);
      try {
        const token = await getToken();
        if (!userUuid) return;
        const schedule = await getDashboardSchedule(userUuid, token, dashboardId);
        setExisting(schedule);
      } catch (caught: unknown) {
        logger.error(
          `ScheduleEmailButton: load existing failed — ${caught instanceof Error ? caught.message : caught}`,
        );
      } finally {
        setIsLoadingExisting(false);
      }
    },
    [getToken, userUuid],
  );

  const handleClick = useCallback(() => {
    if (!userUuid) {
      setToast({ kind: 'error', message: 'Sign in is required to schedule emails.' });
      return;
    }
    if (!activeId) {
      setSaveAsPrompt({ open: true, initialValue: 'My Dashboard' });
      return;
    }
    if (isDirty) {
      setConfirmSaveOpen(true);
      return;
    }
    void openScheduleModal(activeId);
  }, [userUuid, activeId, isDirty, openScheduleModal]);

  const handleSaveAsConfirm = useCallback(
    async (name: string) => {
      setSaveAsPrompt({ open: false, initialValue: '' });
      const result = await saveAs(name);
      if (result) void openScheduleModal(result.id);
    },
    [saveAs, openScheduleModal],
  );

  const handleConfirmSave = useCallback(async () => {
    setConfirmSaveOpen(false);
    const result = await save();
    if (result) void openScheduleModal(result.id);
  }, [save, openScheduleModal]);

  const handleSubmit = useCallback(
    async (args: ScheduleEmailSubmitArgs) => {
      if (!userUuid || !activeId) {
        throw new Error('Cannot save: dashboard is not saved.');
      }
      const token = await getToken();
      try {
        await upsertDashboardSchedule(userUuid, token, activeId, args);
        setScheduleModalOpen(false);
        setExisting(null);
        setToast({
          kind: 'success',
          message: existing ? 'Schedule updated.' : 'Schedule saved.',
        });
      } catch (caught: unknown) {
        logger.error(
          `ScheduleEmailButton: save failed — ${caught instanceof Error ? caught.message : caught}`,
        );
        throw caught instanceof Error ? caught : new Error('Failed to save schedule.');
      }
    },
    [userUuid, activeId, getToken, existing],
  );

  const defaultSubject = activeName
    ? `${activeName} — Market Monitor snapshot`
    : 'Market Monitor snapshot';

  const buttonDisabled = isSaving || isLoadingActive;

  return (
    <>
      <button
        type="button"
        className={styles.button}
        onClick={handleClick}
        disabled={buttonDisabled}
        title="Schedule recurring emails for this dashboard"
        data-testid="schedule-email-button"
      >
        <CalendarClock size={14} aria-hidden="true" />
        <span>Schedule email</span>
      </button>

      <NamePromptModal
        open={saveAsPrompt.open}
        title="Save dashboard before scheduling"
        label="Dashboard name"
        initialValue={saveAsPrompt.initialValue}
        confirmLabel="Save & continue"
        onCancel={() => setSaveAsPrompt({ open: false, initialValue: '' })}
        onConfirm={(name) => {
          void handleSaveAsConfirm(name);
        }}
      />

      {confirmSaveOpen ? (
        <ConfirmSaveDialog
          dashboardName={activeName ?? 'this dashboard'}
          onCancel={() => setConfirmSaveOpen(false)}
          onConfirm={handleConfirmSave}
        />
      ) : null}

      <ScheduleEmailModal
        open={scheduleModalOpen}
        defaultSubject={defaultSubject}
        defaultTo={userEmail ?? ''}
        existing={existing}
        isLoadingExisting={isLoadingExisting}
        onCancel={() => {
          setScheduleModalOpen(false);
          setExisting(null);
        }}
        onSubmit={handleSubmit}
      />

      {toast ? (
        <div
          className={`${styles.toast} ${toast.kind === 'success' ? styles.toastSuccess : styles.toastError}`}
          role="status"
          data-testid={`schedule-email-toast-${toast.kind}`}
        >
          {toast.message}
        </div>
      ) : null}
    </>
  );
};

interface ConfirmSaveDialogProps {
  dashboardName: string;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

const ConfirmSaveDialog: React.FC<ConfirmSaveDialogProps> = ({
  dashboardName,
  onCancel,
  onConfirm,
}) => {
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) onCancel();
  };
  return (
    <div
      className={styles.confirmBackdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
      data-testid="schedule-email-confirm-save"
    >
      <div
        className={styles.confirmModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-email-confirm-save-title"
      >
        <h2 id="schedule-email-confirm-save-title" className={styles.confirmTitle}>
          Unsaved changes
        </h2>
        <p className={styles.confirmBody}>
          {`Save changes to "${dashboardName}" before scheduling the email?`}
        </p>
        <div className={styles.confirmActions}>
          <button
            type="button"
            className={styles.confirmSecondary}
            onClick={onCancel}
            data-testid="schedule-email-confirm-save-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmPrimary}
            onClick={() => {
              void onConfirm();
            }}
            data-testid="schedule-email-confirm-save-confirm"
          >
            Save & continue
          </button>
        </div>
      </div>
    </div>
  );
};
